import { isEmpty } from 'lodash'
import queryString from 'query-string'

import { toast } from '@/components/toasts/toast.helpers'

import { apiService, fetchingAPI } from '../../api'
import * as redux from './audioIngestion.redux'
import {
  FILE_READ_ERROR_MSG,
  friendlyUploadError,
} from '../../views/UploadCalls/audioIngestion.helpers'

const parseMessage = (data) => {
  try {
    return JSON.parse(data)
  } catch {
    return null
  }
}

/**
 * Reserve monthly quota for a batch of uploads.
 *
 * @returns {Promise<string|null>} batch_id on success, null if endpoint doesn't exist (old backend)
 * @throws {Error} QUOTA_EXCEEDED if monthly limit reached (429)
 * @throws {Error} Other errors for unexpected failures
 */
const reserveQuota = async (fileCount, organizationId, dispatch) => {
  try {
    const response = await fetchingAPI(
      `${apiService.audioIngestion}/upload-calls/reserve-quota`,
      'POST',
      dispatch,
      JSON.stringify({ file_count: fileCount, organization_id: organizationId }),
      null, // callback
      { isSilent: true } // suppress default toast - we handle errors ourselves
    )
    return response.batch_id
  } catch (err) {
    // 404 = old backend without quota endpoint, proceed without batch_id
    if (err.status === 404) {
      console.info('Reserve quota endpoint not available, proceeding without batch_id')
      return null
    }
    // 429 = quota exceeded
    if (err.status === 429) {
      const quotaMessage =
        err.data?.detail?.message || "You've reached your monthly limit of call uploads."
      toast.error(`${quotaMessage} Please contact Balto support if you need to exceed the limit.`, {
        cause: err,
      })
      throw new Error('QUOTA_EXCEEDED')
    }
    // Other errors
    console.error('Error reserving quota:', err)
    throw err
  }
}

const streamFile = async (file, socket, dispatch) => {
  dispatch(redux.setFileUploading(file))

  // read before STREAM_START so a file the browser can't read never starts a stream
  const arrayBuffer = await file.arrayBuffer()
  socket.send(JSON.stringify({ action: 'STREAM_START' }))

  const chunkSize = 4096

  for (let offset = 0; offset < arrayBuffer.byteLength; offset += chunkSize) {
    const chunk = arrayBuffer.slice(offset, offset + chunkSize)
    socket.send(chunk)
  }

  socket.send(JSON.stringify({ action: 'STREAM_END' }))
}

export const uploadAudioFiles =
  (values, username, token, userOrgId, setSubmitting) => async (dispatch) => {
    const wsUrl = `${apiService.audioIngestion_websocket}/ws/ingest/upload`
    const { agent, organization, config_cid, agent_channel, files } = values

    // Reserve quota for the entire batch before starting uploads.
    // Note: Quota is consumed per WebSocket connection, not per stream.
    // Language retry reuses the same connection, so it doesn't double-count quota.
    let batchId = null
    try {
      batchId = await reserveQuota(files.length, organization, dispatch)
    } catch (err) {
      if (err.message === 'QUOTA_EXCEEDED') {
        // User already notified via toast, don't start uploads
        setSubmitting?.(false)
        return
      }
      // Unexpected error - don't proceed without rate limiting
      toast.error('Unable to start upload. Please try again.', { cause: err })
      setSubmitting?.(false)
      return
    }

    const metadata = {
      // auth info
      auth_username: username,
      auth_token: token,
      auth_organization_id: userOrgId,
      // call metadata
      user_id: agent,
      organization_id: organization,
      config_cid,
      agent_channel: parseInt(agent_channel, 10),
      // batch_id for quota tracking (null if not available)
      ...(batchId && { batch_id: batchId }),
    }
    dispatch(redux.setFiles(files))

    for (const file of files) {
      const socket = new WebSocket(wsUrl)
      let processComplete = false

      socket.onopen = () => {
        console.info(`WebSocket connected for ${file.name}`)

        // send the data
        socket.send(
          JSON.stringify({
            action: 'UPLOAD_METADATA',
            ...metadata,
            filename: file.name,
          })
        )
      }

      socket.onmessage = async (event) => {
        const msg = parseMessage(event.data)

        switch (msg?.action) {
          case 'STREAM_READY':
            console.info(`Starting stream for ${file.name}`)
            try {
              await streamFile(file, socket, dispatch)
            } catch (err) {
              // usually the file was moved, deleted or changed after it was selected.
              // close the socket so the backend stops waiting for audio
              console.error(`Failed to read ${file.name} for upload`, err)
              dispatch(redux.setFileError({ file, errorMsg: FILE_READ_ERROR_MSG }))
              processComplete = true
              socket.close()
            }
            break
          case 'LANGUAGE_RETRY':
            console.info(`Language retry requested for ${file.name}, waiting for new STREAM_READY`)
            dispatch(redux.setFileLanguageRetried(file))
            break
          case 'POSTCALL_COMPLETE':
            dispatch(redux.setFileDone({ file, callId: msg?.call_id }))
            console.info('Postcall complete:', msg?.call_id)
            processComplete = true
            break
          case 'ERROR': {
            const rawDetail = !isEmpty(msg?.detail) ? msg?.detail : ''
            const errorMsg = friendlyUploadError(rawDetail)
            dispatch(redux.setFileError({ file, errorMsg }))
            console.error('An error has occurred:', msg?.detail)
            // we flag process as complete here so that we don't overwrite this error when the ws closes
            processComplete = true
            break
          }
          default:
            break
        }
      }

      socket.onerror = (err) => {
        toast.error('There was an error uploading your files. Please try again.')
        console.error(`WebSocket error for ${file.name}`, err)
      }

      socket.onclose = () => {
        console.info(`WebSocket closed for file: ${file.name}`)

        if (!processComplete) {
          toast.error('An unexpected error has occurred')
          dispatch(redux.setFileError({ file, errorMsg: friendlyUploadError('') }))
          console.error('An unexpected error has occurred, websocket closed before completion')
        }
      }
    }
  }

export const fetchAudioIngestionRecords =
  (updatedFilters = {}) =>
  async (dispatch, getState) => {
    dispatch(redux.setLoading({ records: true }))

    // Set filters in Redux if passed in
    if (!isEmpty(updatedFilters)) {
      dispatch(redux.setAudioIngestionRecords({ filters: updatedFilters }))
    }

    // Get updated filters from Redux
    const { filters } = getState().audioIngestion.audioIngestion

    // Use passed in values or fallback to filters in Redux
    const params = queryString.stringify({
      start_time: filters.startDate,
      end_time: filters.endDate,
      organization_id: filters.organizationId,
    })
    const settingsParams = queryString.stringify({
      organization_id: filters.organizationId,
    })

    try {
      const { records, total } = await fetchingAPI(
        `${apiService.audioIngestion}/records?${params}`,
        'GET',
        dispatch
      )
      const { last_job_run_time: lastJobRunTime } = await fetchingAPI(
        `${apiService.audioIngestion}/settings?${settingsParams}`
      )

      dispatch(redux.setAudioIngestionRecords({ records, total, lastJobRunTime }))
    } catch (err) {
      console.error('Failed to fetch audio ingestion records', err)
    } finally {
      dispatch(redux.setLoading({ records: false }))
    }
  }
